import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

interface DatePickerProps {
  value?: string;
  onChange?: (date: string) => void;
  placeholder?: string;
  className?: string;
  minYear?: number;
  maxYear?: number;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

function formatDate(y: number, m: number, d: number) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function DatePicker({ value, onChange, placeholder = "Pick a date", className, minYear = 1990, maxYear = 2040 }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'calendar' | 'months' | 'years'>('calendar');
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse initial value
  const parsed = value ? value.split('-').map(Number) : null;
  const [viewYear, setViewYear] = useState(parsed?.[0] || new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState((parsed?.[1] || new Date().getMonth() + 1) - 1);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setView('calendar');
      };
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const calendarDays = useMemo(() => {
    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  }, [daysInMonth, firstDay]);

  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = minYear; y <= maxYear; y++) arr.push(y);
    return arr;
  }, [minYear, maxYear]);

  const handleSelect = (day: number) => {
    const dateStr = formatDate(viewYear, viewMonth, day);
    onChange?.(dateStr);
    setOpen(false);
    setView('calendar');
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const displayText = value
    ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  const today = new Date();
  const todayStr = formatDate(today.getFullYear(), today.getMonth(), today.getDate());
  const selectedDay = parsed?.[2] || null;

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "relative flex w-full items-center rounded-xl border border-light-border dark:border-dark-border",
          "bg-light-elevated dark:bg-dark-elevated",
          "h-11 min-h-[44px] px-3",
          "text-sm text-left",
          "shadow-sm transition-colors",
          "hover:bg-light-elevated dark:hover:bg-dark-elevated",
          "focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500",
          !value && "text-light-text-muted dark:text-dark-text-muted",
          className
        )}
      >
        {displayText || placeholder}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 left-0 bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-xl shadow-xl w-[320px] max-w-[calc(100vw-2rem)]">
          {/* Month/Year header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-light-border dark:border-dark-border">
            <button type="button" onClick={handlePrevMonth} className="p-1.5 rounded-lg hover:bg-light-elevated dark:hover:bg-dark-elevated">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setView(view === 'months' ? 'calendar' : 'months')}
                className="text-sm font-bold px-2 py-1 rounded-lg hover:bg-light-elevated dark:hover:bg-dark-elevated transition-colors"
              >
                {MONTHS[viewMonth]}
              </button>
              <button
                type="button"
                onClick={() => setView(view === 'years' ? 'calendar' : 'years')}
                className="text-sm font-bold px-2 py-1 rounded-lg hover:bg-light-elevated dark:hover:bg-dark-elevated transition-colors"
              >
                {viewYear}
              </button>
            </div>
            <button type="button" onClick={handleNextMonth} className="p-1.5 rounded-lg hover:bg-light-elevated dark:hover:bg-dark-elevated">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Calendar grid */}
          {view === 'calendar' && (
            <div className="p-2.5">
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-1">
                {DAYS.map(d => (
                  <div key={d} className="text-center text-[10px] font-semibold text-light-text-muted py-1">{d}</div>
                ))}
              </div>
              {/* Days */}
              <div className="grid grid-cols-7 gap-0.5">
                {calendarDays.map((day, i) => {
                  if (!day) return <div key={i} />;
                  const dateStr = formatDate(viewYear, viewMonth, day);
                  const isSelected = dateStr === value;
                  const isToday = dateStr === todayStr;

                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSelect(day)}
                      className={cn(
                        "h-9 w-full rounded-lg text-sm font-medium transition-all",
                        isSelected
                          ? "bg-purple-500 text-white shadow-md"
                          : isToday
                            ? "bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 font-bold"
                            : "hover:bg-light-elevated dark:hover:bg-dark-elevated text-light-text dark:text-dark-text"
                      )}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Today button */}
              <div className="mt-2 pt-2 border-t border-light-border dark:border-dark-border flex justify-between">
                <button
                  type="button"
                  onClick={() => {
                    const t = new Date();
                    setViewYear(t.getFullYear());
                    setViewMonth(t.getMonth());
                  }}
                  className="text-xs text-purple-500 px-2 py-1 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-500/10"
                >
                  Today
                </button>
                {value && (
                  <button
                    type="button"
                    onClick={() => { onChange?.(''); setOpen(false); }}
                    className="text-xs text-rose-500 px-2 py-1 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-500/10"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Month picker */}
          {view === 'months' && (
            <div className="p-2.5 grid grid-cols-3 gap-1">
              {MONTHS.map((m, i) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setViewMonth(i); setView('calendar'); }}
                  className={cn(
                    "py-2.5 rounded-lg text-sm font-medium transition-all",
                    i === viewMonth
                      ? "bg-purple-500 text-white shadow-md"
                      : "hover:bg-light-elevated dark:hover:bg-dark-elevated text-light-text dark:text-dark-text"
                  )}
                >
                  {m.slice(0, 3)}
                </button>
              ))}
            </div>
          )}

          {/* Year picker */}
          {view === 'years' && (
            <div className="p-2.5 max-h-[240px] overflow-y-auto grid grid-cols-4 gap-1">
              {years.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => { setViewYear(y); setView('months'); }}
                  className={cn(
                    "py-2 rounded-lg text-sm font-medium transition-all",
                    y === viewYear
                      ? "bg-purple-500 text-white shadow-md"
                      : "hover:bg-light-elevated dark:hover:bg-dark-elevated text-light-text dark:text-dark-text"
                  )}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
